import { useCallback, useEffect, useRef, useState } from 'react';

export interface CallTimer {
  running: boolean;
  /** Seconds counted so far, running or stopped. */
  seconds: number;
  start: () => void;
  /** Stops, and returns the seconds counted. */
  stop: () => number;
  reset: () => void;
}

/**
 * How long the call has lasted, counted from when the caller started it: the
 * queue starts it when a number is dialled, the form's own button otherwise.
 * Counted from the start time, not by adding up ticks, so a background tab
 * whose timers are slowed still records the right length.
 */
export const useCallTimer = (now: () => number = Date.now): CallTimer => {
  const startedAt = useRef<number | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);

  const elapsed = useCallback(() => (startedAt.current == null ? 0 : Math.round((now() - startedAt.current) / 1000)), [now]);

  useEffect(() => {
    if (!running) return undefined;
    const tick = window.setInterval(() => setSeconds(elapsed()), 1000);
    return () => window.clearInterval(tick);
  }, [running, elapsed]);

  const start = useCallback(() => {
    if (startedAt.current != null) return;
    startedAt.current = now();
    setSeconds(0);
    setRunning(true);
  }, [now]);

  // What the last run counted: stopping a stopped timer keeps it.
  const counted = useRef(0);

  const stop = useCallback(() => {
    if (startedAt.current == null) return counted.current;
    counted.current = elapsed();
    startedAt.current = null;
    setRunning(false);
    setSeconds(counted.current);
    return counted.current;
  }, [elapsed]);

  const reset = useCallback(() => {
    counted.current = 0;
    startedAt.current = null;
    setRunning(false);
    setSeconds(0);
  }, []);

  return { running, seconds, start, stop, reset };
};
